import { Link } from "react-router-dom";
import { Target, Eye, Gem, ShieldCheck, Lightbulb, Award, Users, Globe, Leaf, Building2, ArrowRight } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=2400&q=85";
const WHO_WE_ARE_IMG = "https://images.unsplash.com/photo-1590247813693-5541d1c609fd?auto=format&fit=crop&w=1800&q=85";

const VALUES = [
  "Excellence in engineering and quality",
  "Innovation that solves complex challenges",
  "Integrity through honesty and accountability",
  "Sustainability for long-term value",
  "Safety at every stage of delivery",
];

const REASONS = [
  { icon: Users, title: "Multidisciplinary Expertise", copy: "Engineers, technologists and marine specialists combined." },
  { icon: Lightbulb, title: "Innovative Technology", copy: "Modern methods and forward-thinking marine engineering." },
  { icon: ShieldCheck, title: "Reliable Performance", copy: "Infrastructure engineered for demanding marine environments." },
  { icon: Award, title: "Complete Project Delivery", copy: "One trusted partner from concept to commissioning." },
  { icon: Globe, title: "International Standards", copy: "Globally recognised engineering principles and best practices." },
  { icon: Leaf, title: "Long-Term Maintenance", copy: "Ongoing support that protects your investment for years." },
];

const STATS = [
  { icon: Building2, k: "100+", v: "Projects Delivered" },
  { icon: Users, k: "50+", v: "Happy Clients Across Africa" },
  { icon: Globe, k: "10+", v: "Countries Served" },
  { icon: Award, k: "15+", v: "Years Of Combined Experience" },
];

// Accurate Africa continent outline with subtle dot pattern
const AfricaShape = ({ className }) => (
  <svg viewBox="0 0 400 470" className={className} aria-hidden="true">
    <defs>
      <pattern id="mia-africa-dots" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
        <circle cx="2" cy="2" r="1.2" fill="currentColor" opacity="0.35" />
      </pattern>
    </defs>
    <path
      d="M78 62 C115 48 165 42 195 42 C235 42 275 50 300 68 C315 78 328 92 330 110 C336 118 348 128 350 138 C350 148 342 152 336 150 C340 158 352 168 358 180 C362 190 355 196 344 192 C338 200 330 218 328 236 C326 254 316 272 302 288 C292 305 286 322 282 340 C276 362 268 380 255 400 C240 422 220 442 200 448 C182 450 168 442 158 428 C150 415 148 400 148 382 C148 362 148 342 142 324 C136 308 128 296 122 285 C115 270 118 255 128 245 C138 236 148 232 150 222 C148 212 140 208 128 210 C108 214 88 222 78 220 C65 218 55 208 58 195 C60 180 62 168 58 152 C55 138 56 122 60 108 C62 92 68 78 78 62 Z"
      fill="url(#mia-africa-dots)"
    />
    <path
      d="M330 300 C338 298 344 306 342 320 C338 340 332 360 322 372 C318 366 320 350 322 336 C324 320 324 308 330 300 Z"
      fill="url(#mia-africa-dots)"
    />
  </svg>
);

export default function About() {
  return (
    <div>
      {/* HERO */}
      <section className="relative bg-[color:var(--mia-navy)] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Waterfront" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--mia-navy)] via-[color:var(--mia-navy)]/85 to-[color:var(--mia-navy)]/25" />
        </div>
        <div className="mia-container py-14 lg:py-24 relative">
          <div className="text-[0.72rem] font-bold tracking-[0.24em] uppercase text-[color:var(--mia-green)]">About Us</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02] tracking-tight max-w-3xl">
            Building Stronger Waterways.<br />
            Creating Lasting <span className="text-[color:var(--mia-green)]">Impact.</span>
          </h1>
          <div className="w-16 h-1 bg-[color:var(--mia-green)] my-7" />
          <p className="text-white/85 max-w-xl leading-relaxed">
            Marine Infrastructure Africa is a specialist marine engineering and infrastructure company
            dedicated to developing safer, smarter and more sustainable waterways across Africa. We
            deliver innovative infrastructure solutions that support transportation, commerce, tourism,
            energy, public safety and waterfront development.
          </p>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="py-14 lg:py-16 bg-white">
        <div className="mia-container grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="font-display font-black text-[color:var(--mia-navy)] text-2xl tracking-tight uppercase">Who We Are</div>
            <div className="w-10 h-[3px] bg-[color:var(--mia-green)] mt-3 mb-6" />
            <p className="text-[color:var(--mia-text)] leading-relaxed">
              We help governments, industries and investors unlock the full potential of Africa's marine
              economy. With a commitment to engineering excellence and long-term sustainability, we deliver
              projects that improve navigation, strengthen coastal resilience and enhance connectivity
              between land and water. From initial concept and feasibility studies through engineering,
              procurement, installation and long-term maintenance, we provide complete project delivery
              under one trusted partner.
            </p>
          </div>
          <div className="rounded-md overflow-hidden aspect-[4/3] shadow-xl">
            <img src={WHO_WE_ARE_IMG} alt="Marine Infrastructure Africa office" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* MISSION VISION VALUES */}
      <section className="py-14 lg:py-16 bg-[color:var(--mia-grey)]">
        <div className="mia-container">
          <h2 className="section-title text-2xl sm:text-3xl">OUR MISSION, VISION & VALUES</h2>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { icon: Target, title: "Our Mission", copy: "To design, supply and deliver innovative marine infrastructure that enhances safety, accessibility, resilience and prosperity across Africa's waterways and coastal communities.", list: null },
              { icon: Eye, title: "Our Vision", copy: "To become Africa's most trusted marine infrastructure company, delivering world-class engineering solutions that transform waterways into engines of sustainable economic growth and regional development.", list: null },
              { icon: Gem, title: "Our Core Values", copy: null, list: VALUES },
            ].map((c) => (
              <div key={c.title} className="bg-white rounded-md p-8 text-center card-lift" data-testid={`mvv-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="icon-round-light mx-auto"><c.icon className="w-7 h-7" strokeWidth={1.7} /></div>
                <h3 className="font-display font-bold text-[color:var(--mia-navy)] mt-5 text-base tracking-[0.14em] uppercase">{c.title}</h3>
                {c.copy && <p className="text-sm text-[color:var(--mia-muted)] mt-4 leading-relaxed">{c.copy}</p>}
                {c.list && (
                  <ul className="mt-5 space-y-2.5 text-left inline-block">
                    {c.list.map((v) => (
                      <li key={v} className="flex items-start gap-2.5 text-sm text-[color:var(--mia-text)]">
                        <span className="mt-0.5 w-4 h-4 rounded-full bg-[color:var(--mia-navy)] text-white text-[10px] font-bold flex items-center justify-center shrink-0">✓</span>
                        {v}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY PARTNER WITH US */}
      <section className="py-14 lg:py-16 bg-[color:var(--mia-grey)] pt-0">
        <div className="mia-container">
          <h2 className="section-title text-2xl sm:text-3xl">WHY PARTNER WITH US?</h2>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {REASONS.map((r) => (
              <div key={r.title} className="text-center" data-testid={`why-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="mx-auto w-14 h-14 flex items-center justify-center text-[color:var(--mia-navy)]"><r.icon className="w-8 h-8" strokeWidth={1.5} /></div>
                <h3 className="font-display font-bold text-[color:var(--mia-navy)] mt-4 text-xs sm:text-[0.78rem] tracking-[0.14em] uppercase leading-tight">{r.title}</h3>
                <p className="text-xs text-[color:var(--mia-muted)] mt-3 leading-relaxed">{r.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR JOURNEY */}
      <section className="py-14 lg:py-16 bg-[color:var(--mia-navy)] text-white">
        <div className="mia-container grid lg:grid-cols-[1fr,1.6fr] gap-10 items-center">
          <div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight uppercase">Our Journey</h2>
            <div className="w-12 h-1 bg-[color:var(--mia-green)] my-5" />
            <p className="text-white/80 leading-relaxed max-w-md">
              Our multidisciplinary team combines engineering expertise, innovative technology and practical
              experience to deliver infrastructure that performs reliably in demanding marine environments.
              Every project we deliver reinforces our commitment to Africa's Blue Economy.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="about-stats">
            {STATS.map((s) => (
              <div key={s.v} className="text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-white/10 text-[color:var(--mia-green)] flex items-center justify-center"><s.icon className="w-6 h-6" strokeWidth={1.7} /></div>
                <div className="font-display font-black text-white text-4xl sm:text-5xl mt-4">{s.k}</div>
                <div className="text-[0.68rem] font-bold tracking-[0.14em] uppercase text-white/70 mt-2 leading-tight">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR COMMITMENT TO AFRICA */}
      <section className="py-14 lg:py-16 bg-white relative overflow-hidden">
        <AfricaShape className="absolute right-8 lg:right-24 top-1/2 -translate-y-1/2 w-60 h-60 lg:w-96 lg:h-96 text-[color:var(--mia-navy)]" />
        <div className="mia-container relative">
          <h2 className="font-display font-black text-2xl sm:text-3xl text-[color:var(--mia-navy)] tracking-tight uppercase max-w-lg">Build The Future With Us</h2>
          <div className="w-10 h-[3px] bg-[color:var(--mia-green)] mt-3 mb-6" />
          <p className="text-[color:var(--mia-muted)] max-w-xl leading-relaxed">
            We are committed to supporting Africa's Blue Economy by delivering infrastructure that improves
            navigation safety, strengthens coastal resilience, enhances tourism, promotes sustainable
            investment and creates lasting value for future generations. At Marine Infrastructure Africa,
            we don't simply build marine infrastructure — we build opportunities, connect communities and
            create lasting impact across Africa's waterways.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/request-consultation" className="mia-btn mia-btn-primary" data-testid="about-cta-consultation">Request Consultation <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/solutions" className="mia-btn mia-btn-outline-navy" data-testid="about-cta-solutions">Explore Our Solutions</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
